<template>
  <SalesPageShell
    :title="text.page.title"
    :subtitle="text.page.subtitle"
    :actions="headerActions"
    @action="onHeaderAction"
  >
    <template #metrics>
      <section class="metrics-grid">
        <SalesKpiCard
          v-for="metric in kpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <SalesFlatRatesToolbar
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />
    </template>

    <template #tabs>
      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <SalesFlatRatesTable
        v-if="isOpenStatusTab"
        :rows="paginatedRows"
        :loading="tableLoading"
        :pagination="tablePagination"
        :empty-text="text.table.empty"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @invoice-sale="handleInvoiceSale"
        @simple-note-sale="handleSimpleNoteSale"
        @pay-sale="handlePaySale"
        @change-sale-status="handleChangeSaleStatus"
        @edit-sale="handleEditSale"
        @view-sale="handleViewSale"
        @delete-sale="handleDeleteSale"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />

      <SharedSalesTable
        v-else
        :rows="paginatedRows"
        :loading="tableLoading"
        :pagination="tablePagination"
        :empty-text="businessTableEmptyText"
        :show-customer-column="true"
        :sort-field="businessSortField"
        :sort-direction="businessSortDirection"
        @change-sale-status="handleBusinessChangeSaleStatus"
        @edit-sale="handleBusinessEditSale"
        @delete-sale="handleBusinessDeleteSale"
        @page-change="handlePageChange"
        @sort-change="handleBusinessSortChange"
      />
    </template>

    <template #modal>
      <SaleModal
        :show="showCreateFlatRateModal"
        :mode="saleModalMode"
        :initial-data="saleModalInitialData"
        :sale-kind="saleModalKind"
        service-filter-mode="flat-rate"
        :submitting="createFlatRateSubmitting"
        :advertising-enabled="isAdvertisingModuleActive"
        @close="closeCreateFlatRateModal"
        @delete="handleDeleteSaleFromModal"
        @submit="handleCreateFlatRate"
      />

      <PaymentPlanModal
        :show="isPaymentPlanModalOpen"
        mode="edit"
        :initial-data="editingPaymentPlan"
        :sale-id="editingPaymentSaleId"
        :sale-price="editingPaymentSalePrice"
        :existing-planned-total="editingExistingPlannedTotal"
        :submitting="isPaymentPlanSubmitting"
        @close="closePaymentPlanModal"
        @submit="submitPaymentPlanEdit"
      />

      <ConfirmActionModal
        :show="isDeleteConfirmModalOpen"
        :title="text.confirms.delete.title"
        :subtitle="text.confirms.delete.subtitle"
        :message="deleteConfirmMessage"
        :confirm-text="text.confirms.delete.confirm"
        @cancel="closeDeleteConfirmModal"
        @confirm="confirmDeleteSale"
      />

      <SaleStatusModal
        :show="isStatusModalOpen"
        :sale-name="resolveSelectedSaleName()"
        :current-status-label="resolveSelectedSaleStatusLabel()"
        :statuses="statusOptions"
        :selected-status-id="resolveSelectedSaleStatusId()"
        :submitting="statusModalSubmitting"
        @close="closeStatusModal"
        @submit="confirmUpdateSaleStatus"
      />
    </template>
  </SalesPageShell>
</template>

<script>
import SalesKpiCard from '@/components/sales/SalesKpiCard.vue';
import SalesPageShell from '@/components/sales/SalesPageShell.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import PaymentPlanModal from '@/components/sales/shared/paymentPlan/PaymentPlanModal.vue';
import SaleStatusModal from '@/components/sales/shared/SaleStatusModal.vue';
import SaleModal from '@/components/sales/SaleModal.vue';
import SalesFlatRatesToolbar from '@/components/salesFlatRates/SalesFlatRatesToolbar.vue';
import SalesFlatRatesTable from '@/components/salesFlatRates/SalesFlatRatesTable.vue';
import SharedSalesTable from '@/components/sales/shared/SalesTable.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  createSalesFlatRatesPageState,
  salesFlatRatesPageComputed,
  salesFlatRatesPageMethods,
} from '@/composables/salesFlatRates/useSalesFlatRatesPage';

const { catalog: text } = useDomainText('flatRates');

export default {
  name: 'SalesFlatRatesPage',
  components: {
    SalesKpiCard,
    SalesPageShell,
    ConfirmActionModal,
    PaymentPlanModal,
    SaleStatusModal,
    SaleModal,
    SalesFlatRatesToolbar,
    SalesFlatRatesTable,
    SharedSalesTable,
    StatusTabs,
  },
  data() {
    return {
      text,
      ...createSalesFlatRatesPageState(text),
    };
  },
  computed: {
    ...salesFlatRatesPageComputed,
    isAdvertisingModuleActive() {
      const can = typeof this.$can === 'function' ? this.$can : null;
      if (!can) {
        return true;
      }

      return can('sales.advertising');
    },
  },
  mounted() {
    this.fetchRows();
    this.fetchBusinessRows({ silent: true });
  },
  methods: {
    ...salesFlatRatesPageMethods,
  },
};
</script>
